import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { useSelector } from "src/store";
import { setPaneTab, showDetails, updateSearchQuery } from "src/ui/containers/dashboard/devices/store";
import { selectDevicesByGroupIds } from "src/ui/containers/dashboard/devices/store/selectors/devices";
import { MinimalTableCell } from "src/ui/shared/table/MinimalTableComponents";
import { makeStyles } from "tss-react/mui";

import { Device, DeviceState } from "@dashboard/devices/types";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { Theme, Tooltip, Typography } from "@mui/material";
import ElasticService from "@services/elasticService";
import UserSettingsService from "@services/userSettingsService";
import QueryBuilder from "@shared/form/QueryBuilderField/QueryBuilderField.utils";

import { TransformedApp } from "./ManagedAppsCard";

const useStyles = makeStyles()((theme: Theme) => ({
    link: { color: theme.palette.emteria.link, cursor: "pointer", padding: 0, display: "inline-block" },
    border: { borderBottom: `1px solid ${theme.palette.emteria.border.secondary}` },
}));

const hasCorrectPackage = (app: TransformedApp, packages: DeviceState["packages"]) => {
    return packages.some((item) => item.package === app.package && item.versionCode === app.versionCode);
};

const getDeviceWithCorrectApp = (app: TransformedApp, devices: (Device | null)[]) => {
    return devices.filter((item) => (item?.lastState ? hasCorrectPackage(app, item.lastState.packages) : false))[0];
};

interface TableItemProps {
    count: number;
    version: number;
    apps: TransformedApp;
    filteredGroupIds: number[];
}

const TableItem = (props: TableItemProps) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["common"]);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const groups = useSelector(selectGroups);
    const filteredDevices = useSelector((state) => selectDevicesByGroupIds(state, props.filteredGroupIds));
    const filteredGroups = groups.filter((group) => props.filteredGroupIds.includes(group.id));

    const handleLicensesOnClick = () => {
        const filters = QueryBuilder.parseFiltersToString([
            {
                key: "device.lastState.packages.versionCode",
                operator: "=",
                value: String(props.apps.versionCode),
            },
            { key: "device.lastState.packages.package", operator: "=", value: props.apps.package },
        ]);
        dispatch(updateSearchQuery(filters));
        navigate(`/dashboard/devices?search=${encodeURIComponent(filters)}`);
    };

    const handleLicenseOnClick = (app: TransformedApp) => {
        const device = getDeviceWithCorrectApp(app, filteredDevices);
        if (!device) {
            return;
        }

        const group = filteredGroups.find((g) => g.licenseIds.includes(device.licenseId));
        if (group) {
            UserSettingsService.setWorkspaceValue("devicePage.columnFilters", []);

            dispatch(setPaneTab("APPS"));
            dispatch(showDetails({ type: "license", selectedId: device.licenseId }));
            handleLicensesOnClick();
            ElasticService.createTransaction("overviewPageLinkClick", {
                tab: "managed-applications",
                text: props.count,
            });
        }
    };

    if (props.count === 0) {
        return (
            <MinimalTableCell className={classes.border} align="right">
                <Typography>0</Typography>
            </MinimalTableCell>
        );
    }

    if (props.count === 1) {
        return (
            <MinimalTableCell className={classes.border} align="right">
                <Tooltip title={t("common:tooltip.showDevices", { count: 1 })}>
                    <span className={classes.link} onClick={() => handleLicenseOnClick(props.apps)}>
                        {props.count}
                    </span>
                </Tooltip>
            </MinimalTableCell>
        );
    }

    return (
        <MinimalTableCell className={classes.border} align="right">
            <Tooltip title={t("common:tooltip.showDevices", { count: props.count })}>
                <span className={classes.link} onClick={handleLicensesOnClick}>
                    {props.count}
                </span>
            </Tooltip>
        </MinimalTableCell>
    );
};

export default TableItem;
